import { useMemo, useState } from 'react';
import { Button, Checkbox, Empty, Input, Popover } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { removeAccents } from '../../services/mixin';
import { chipBackground, readableTagColor, tagShortName, type TagOption } from './types';
import useIsDark from './useIsDark';
import { MUTED_TEXT_BUTTON, brandButton } from './brand';

interface Props {
    title: string;
    subtitle: string;
    tags: TagOption[];
    /** Các thẻ không chọn được, kèm lý do hiện bên phải dòng */
    disabledReasons?: Record<number, string>;
    applyLabel?: string;
    onApply: (ids: number[]) => void;
    children: React.ReactNode;
}

/** Popover chọn nhiều thẻ trạng thái cùng lúc, có ô tìm kiếm không dấu. */
export default function TagPicker({
    title,
    subtitle,
    tags,
    disabledReasons = {},
    applyLabel = 'Áp dụng',
    onApply,
    children,
}: Props) {
    const isDark = useIsDark();
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [selected, setSelected] = useState<number[]>([]);

    const visible = useMemo(() => {
        const kw = removeAccents(keyword.trim().toLowerCase());
        if (!kw) return tags;
        return tags.filter((t) =>
            removeAccents(`${t.code ?? ''} ${t.name}`.toLowerCase()).includes(kw)
        );
    }, [tags, keyword]);

    const selectableVisible = visible.filter((t) => !disabledReasons[t.id]);

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        if (!next) {
            setKeyword('');
            setSelected([]);
        }
    };

    const toggle = (id: number) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const selectAllVisible = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            selectableVisible.forEach((t) => next.add(t.id));
            return Array.from(next);
        });
    };

    const apply = () => {
        onApply(selected);
        handleOpenChange(false);
    };

    const content = (
        <div className="w-[300px]">
            <div className="px-1 pb-2 border-b border-gray-100 dark:border-[#2a2a2a]">
                <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">{title}</div>
                <div className="text-[11px] text-gray-400 dark:text-[#8a8a8a] mt-0.5">{subtitle}</div>
                <Input
                    allowClear
                    size="small"
                    className="mt-2"
                    prefix={<SearchOutlined className="text-gray-400" />}
                    placeholder="Tìm mã hoặc tên trạng thái..."
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                />
            </div>

            <div className="max-h-[260px] overflow-y-auto py-1.5">
                {visible.length === 0 && (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Không có trạng thái khớp"
                        className="!my-4"
                    />
                )}
                {visible.map((t) => {
                    const reason = disabledReasons[t.id];
                    const color = readableTagColor(t.color, isDark);
                    return (
                        <button
                            key={t.id}
                            type="button"
                            disabled={!!reason}
                            onClick={() => toggle(t.id)}
                            className={[
                                'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-left',
                                reason
                                    ? 'opacity-45 cursor-not-allowed'
                                    : 'hover:bg-gray-50 dark:hover:bg-[#262626]',
                            ].join(' ')}
                        >
                            <Checkbox
                                checked={selected.includes(t.id)}
                                disabled={!!reason}
                                className="pointer-events-none"
                            />
                            {t.code && (
                                <span
                                    className="font-mono text-[11px] px-1.5 py-0.5 rounded shrink-0"
                                    style={{ backgroundColor: chipBackground(t.color, isDark), color }}
                                >
                                    {t.code}
                                </span>
                            )}
                            <span className="text-xs text-gray-700 dark:text-gray-200 truncate">
                                {tagShortName(t, t.id)}
                            </span>
                            {reason && (
                                <span className="ml-auto text-[10px] text-gray-400 dark:text-[#6b6b6b] shrink-0">
                                    {reason}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-[#2a2a2a]">
                <Button
                    size="small"
                    type="text"
                    className={MUTED_TEXT_BUTTON}
                    onClick={selectAllVisible}
                    disabled={!selectableVisible.length}
                >
                    Chọn tất cả
                </Button>
                <span className="flex-1" />
                <Button
                    size="small"
                    type="primary"
                    disabled={!selected.length}
                    onClick={apply}
                    className={brandButton(selected.length > 0)}
                >
                    {selected.length ? `${applyLabel} (${selected.length})` : applyLabel}
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            open={open}
            onOpenChange={handleOpenChange}
            trigger="click"
            placement="bottomLeft"
            content={content}
        >
            {children}
        </Popover>
    );
}
